import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Gem, Heart, Trophy, Volume2, VolumeX, RefreshCw, Play } from 'lucide-react';
import { arcadeSave, isLoggedIn } from '../api';
import { useToast } from '../hooks/use-toast';

const GEM_COLORS = ['#f0d582', '#c9a2d4', '#7bb661', '#ff8fb3', '#8fb8ff', '#f5b971'];
const ACHIEVEMENTS = [
  { score: 10, label: 'First Sparkle' },
  { score: 25, label: 'Crystal Collector' },
  { score: 50, label: 'Energy Adept' },
  { score: 100, label: 'Prestige Master' },
];

let _audioCtx = null;
const beep = (on, freq = 660) => {
  if (!on) return;
  try {
    _audioCtx = _audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const o = _audioCtx.createOscillator(); const g = _audioCtx.createGain();
    o.frequency.value = freq; o.type = 'sine'; o.connect(g); g.connect(_audioCtx.destination);
    g.gain.setValueAtTime(0.08, _audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, _audioCtx.currentTime + 0.15);
    o.start(); o.stop(_audioCtx.currentTime + 0.15);
  } catch { /* ignore */ }
};

export const CrystalCatch = () => {
  const { toast } = useToast();
  const [state, setState] = useState('idle'); // idle | playing | over
  const [gems, setGems] = useState([]);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [best, setBest] = useState(Number(localStorage.getItem('pe_cc_best') || 0));
  const [sound, setSound] = useState(true);
  const [earned, setEarned] = useState([]);
  const idRef = useRef(0);
  const loopRef = useRef(null);
  const spawnRef = useRef(null);
  const scoreRef = useRef(0);

  const stop = useCallback(() => { clearInterval(loopRef.current); clearInterval(spawnRef.current); }, []);

  const end = useCallback(async () => {
    stop(); setState('over');
    const finalScore = scoreRef.current;
    if (finalScore > best) { setBest(finalScore); localStorage.setItem('pe_cc_best', String(finalScore)); }
    if (isLoggedIn() && finalScore > 0) {
      try { await arcadeSave('crystal-catch', 'Crystal Catch', { score: finalScore, best: Math.max(finalScore, best) }); } catch { /* ignore */ }
    }
  }, [best, stop]);

  const start = () => {
    setScore(0); scoreRef.current = 0; setLives(3); setLevel(1); setGems([]); setEarned([]); setState('playing');
  };

  useEffect(() => {
    if (state !== 'playing') return;
    const lvl = Math.floor(scoreRef.current / 10) + 1;
    loopRef.current = setInterval(() => {
      setGems((prev) => {
        const speed = 2 + lvl * 0.6;
        const moved = prev.map((g) => ({ ...g, y: g.y + speed }));
        const missed = moved.filter((g) => g.y >= 100);
        if (missed.length) {
          setLives((L) => {
            const nl = L - missed.length;
            if (nl <= 0) { setTimeout(end, 0); }
            return Math.max(0, nl);
          });
          beep(sound, 200);
        }
        return moved.filter((g) => g.y < 100);
      });
    }, 60);
    spawnRef.current = setInterval(() => {
      idRef.current += 1;
      setGems((prev) => [...prev, { id: idRef.current, x: 8 + Math.random() * 82, y: -6, color: GEM_COLORS[idRef.current % GEM_COLORS.length] }]);
    }, Math.max(500, 1300 - lvl * 90));
    return stop;
  }, [state, level, sound, end, stop]);

  const catchGem = (id) => {
    setGems((prev) => prev.filter((g) => g.id !== id));
    beep(sound, 760);
    setScore((s) => {
      const ns = s + 1; scoreRef.current = ns;
      const nl = Math.floor(ns / 10) + 1;
      if (nl !== level) { setLevel(nl); toast({ title: `Level ${nl}! ✨`, description: 'The crystals fall faster now.' }); }
      ACHIEVEMENTS.forEach((a) => { if (ns === a.score) { setEarned((e) => [...e, a.label]); toast({ title: `Achievement: ${a.label} 🏆` }); } });
      return ns;
    });
  };

  return (
    <div data-testid="game-crystal-catch">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-4 font-jost text-sm text-[#f5ecd9]/80">
          <span className="flex items-center gap-1"><Trophy className="h-4 w-4 text-[#f0d582]" /> {score}</span>
          <span className="flex items-center gap-1">{Array.from({ length: 3 }).map((_, i) => <Heart key={i} className={`h-4 w-4 ${i < lives ? 'text-[#ff4da6] fill-[#ff4da6]' : 'text-white/15'}`} />)}</span>
          <span className="text-[#f5ecd9]/50">Lv {level}</span>
          <span className="text-[#f5ecd9]/50">Best {best}</span>
        </div>
        <button data-testid="cc-sound" onClick={() => setSound((s) => !s)} className="text-[#f5ecd9]/60 hover:text-[#f0d582]">{sound ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}</button>
      </div>

      <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-b from-[#160f14] to-[#0c0809] border border-[#d4af37]/20 select-none" style={{ height: 380, touchAction: 'manipulation' }}>
        {state === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <Gem className="h-12 w-12 text-[#f0d582] mb-3 floaty" />
            <h3 className="font-cinzel text-2xl text-gold-gradient">Crystal Catch</h3>
            <p className="font-cormorant text-lg text-[#f5ecd9]/65 mt-1 max-w-sm">Tap the falling crystals before they slip away. Miss three and the ritual ends. Faster every level.</p>
            <button data-testid="cc-start" onClick={start} className="mt-5 btn-gold rounded-full px-7 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]"><Play className="h-4 w-4 inline mr-1" /> Play</button>
          </div>
        )}
        {state === 'playing' && gems.map((g) => (
          <button key={g.id} data-testid="cc-gem" onClick={() => catchGem(g.id)} className="absolute h-10 w-10 rounded-full flex items-center justify-center active:scale-90 transition-transform" style={{ left: `${g.x}%`, top: `${g.y}%`, background: `radial-gradient(circle at 30% 30%, ${g.color}, ${g.color}55)`, boxShadow: `0 0 14px ${g.color}99` }}>
            <Gem className="h-5 w-5 text-white/90" />
          </button>
        ))}
        {state === 'over' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-black/40">
            <p className="font-jost text-[11px] uppercase tracking-[0.3em] text-[#ff4da6]">Ritual Complete</p>
            <h3 className="font-cinzel text-4xl text-gold-gradient mt-1" data-testid="cc-final">{score}</h3>
            <p className="font-cormorant text-lg text-[#f5ecd9]/70">crystals caught {score >= best && score > 0 ? '· New best! ✨' : `· Best ${best}`}</p>
            {earned.length > 0 && <p className="font-jost text-xs text-[#f0d582] mt-2">Unlocked: {earned.join(', ')}</p>}
            <button data-testid="cc-replay" onClick={start} className="mt-5 btn-gold rounded-full px-7 py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]"><RefreshCw className="h-4 w-4 inline mr-1" /> Play Again</button>
            {!isLoggedIn() && <p className="font-jost text-[11px] text-[#f5ecd9]/45 mt-3">Log in to save your best score.</p>}
          </div>
        )}
      </div>
      <p className="font-jost text-[11px] text-[#f5ecd9]/40 mt-3 text-center">A skill game for fun & focus — engagement only, it does not mint reward points (that keeps Prestige Rewards fair). Your best score is saved to your account.</p>
    </div>
  );
};

export default CrystalCatch;
